"use client";

import { React, useState } from "react";
import { useRouter } from "next/navigation";
import parser from "html-react-parser";
import SelectField from "./SelectField";
import TextField from "./TextField";
import PlacesAutocompleteField from "./PlacesAutocompleteField";
import { validateFormData } from "@/helpers/formValidator";
import { usePathname } from "next/navigation";
import { getImageURL } from "@/helpers/imageURLHelper";

const Masthead = ({
  title,
  headerLevel,
  headerTitle,
  text,
  fontColor,
  backGroundColor,
  backgroundImage,
  hasForm,
}) => {
  // console.log('mForm', formData);
  const formValidationTemplate = {
    location: {
      required: true,
      valid: true,
      message: "",
      type: "text",
    },
    discipline: {
      required: true,
      valid: true,
      message: "",
      type: "text",
    },
    fullName: {
      required: false,
      valid: true,
      message: "",
      type: "text",
    },
    lessonLocation: {
      required: false,
      valid: true,
      message: "",
      type: "text",
    },
  };

  const router = useRouter();
  const [place, setPlace] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [inputsData, setInputsData] = useState({});
  const [inputsValidationData, setInputsValidationData] = useState(
    formValidationTemplate
  );
  const [autocompleteError, setAutocompleteError] = useState("");
  const action = "/find-a-teacher/place";
  const pathname = usePathname();
  const isSC = pathname.includes("/study-rcm-simplified-chinese");
  const isTC = pathname.includes("/study-rcm-traditional-chinese");

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInputsData({
      ...inputsData,
      [name]: value,
    });
    setInputsValidationData((prevInputsData) => ({
      ...prevInputsData,
      [name]: { ...prevInputsData[name], value: value },
    }));
  };

  const onLoad = (autocompleteObj) => {
    setAutocomplete(autocompleteObj);
  };

  const onPlaceChanged = (e) => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      setSelectedPlace(place);
      setPlace(place.formatted_address);
      setAutocompleteError("");
    }
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    const data = validateFormData(inputsValidationData);
    setInputsValidationData(data);

    const inputsDataUrlencoded = inputsData
      ? Object.keys(inputsData)
          .map(
            (key) =>
              encodeURIComponent(key) +
              "=" +
              encodeURIComponent(inputsData[key])
          )
          .join("&")
      : null;

    if (selectedPlace) {
      if ("place_id" in selectedPlace) {
        router.push(
          `${action}?place=${selectedPlace.place_id}` +
            (inputsDataUrlencoded ? `&${inputsDataUrlencoded}` : "")
        ); //moving forward to next route
      }
      return;
    } else {
      setAutocompleteError("This field is required");
      return;
    }
  };

  const header = headerTitle
    ? `<${headerLevel}>${headerTitle}</${headerLevel}>`
    : "";

  return (
    <div className="find-a-teacher-body tps-body" id="body">
      <h1 className="hide no-print">{title}</h1>

      <div className="tps-masthead">
        <div className="fatv2-masthead-wrapper">
          <div
            className="fatv2-masthead-inner"
            style={{
              backgroundImage: `url(${getImageURL(backgroundImage.url)})`,
            }}
          >
            <div className={"grid-container"}>
              <div className={"grid-x"}>
                <div className={"cell large-6"}>
                  <div
                    className={"fatv2-masthead-text"}
                    style={{
                      color: fontColor ? fontColor : "#fff",
                      backgroundColor: backGroundColor
                        ? backGroundColor
                        : "transparent",
                    }}
                  >
                    {parser(header)}
                    {parser(text)}
                  </div>
                </div>
                {hasForm && (
                  <div className="cell large-6">
                    <div className="fatv2-masthead-form-wrapper">
                      <div className="fatv2-masthead-form-inner">
                        <div
                          className="fatv2-masthead-form fatv2-masthead-fake-form"
                          data-algo="geo"
                          data-algo-geofield="#postal-code"
                          data-algo-georadiusfield="#distance"
                          data-algo-index="find-a-teacher-v4"
                          id="findateacher-fatlp-form"
                        >
                          <form onSubmit={submitHandler} noValidate>
                            <fieldset className="fatv2-masthead-form-fieldset">
                              <legend className="h2 fatv2-masthead-form-legend">
                                {isSC
                                  ? "寻找在您附近的加拿大皇家音乐学院老师"
                                  : isTC
                                    ? "尋找在您附近的加拿大皇家音樂學院老師"
                                    : "Music Lessons With RCM Teachers"}
                              </legend>
                              <p>
                                {isSC
                                  ? "输入您的信息开始寻找"
                                  : isTC
                                    ? "輸入您的資料後開始尋找"
                                    : "Enter your details to find a teacher or school in our National Teacher Directory."}
                              </p>
                              <div
                                className="form-container-inner"
                                style={{ display: "block" }}
                                data-tps-step-index="0"
                              >
                                <div className="fatv2-masthead-form-row">
                                  <SelectField
                                    label={
                                      isSC
                                        ? "乐器/学科"
                                        : isTC
                                          ? "樂器／學科"
                                          : "Instrument/Discipline"
                                    }
                                    name={"disciplines"}
                                    idField={"disciplines"}
                                    required={true}
                                    onChangeHandler={handleInputChange}
                                    externalDataSource={"disciplines"}
                                    labelClassName={"fatv2-masthead-form-label"}
                                    selectClassName={
                                      "form-select fatv2-masthead-form-select"
                                    }
                                    isValid={true}
                                    errorMessage="This field is required"
                                    customErrorInputStyle={{
                                      margin: "0",
                                    }}
                                    customErrorLabelStyle={{}}
                                    customErrorMessageStyle={{
                                      textTransform: "uppercase",
                                      fontSize: "smaller",
                                      padding: "0 0 1rem",
                                      display: "inline-block",
                                    }}
                                  />
                                </div>
                                <PlacesAutocompleteField
                                  labelClassName="fatv2-masthead-form-label tps-form-label tps-field-required"
                                  inputClassName="form-text fatv2-masthead-form-input tps-form-input"
                                  label={
                                    isSC
                                      ? "地点"
                                      : isTC
                                        ? "地點"
                                        : "Your Location"
                                  }
                                  name="location"
                                  idField={"location"}
                                  required={true}
                                  onPlaceChanged={onPlaceChanged}
                                  placeholder={
                                    isSC
                                      ? "输入位置"
                                      : isTC
                                        ? "輸入位置"
                                        : "Enter your location"
                                  }
                                  onLoad={onLoad}
                                  autoComplete="off"
                                  // value={place}
                                  setValue={(e) => {
                                    setPlace(e.target.value);
                                  }}
                                  isValid={
                                    inputsValidationData["location"]?.valid
                                  }
                                  errorMessage={autocompleteError}
                                  customErrorInputStyle={{
                                    margin: "0",
                                  }}
                                  customErrorLabelStyle={{}}
                                  customErrorMessageStyle={{
                                    textTransform: "uppercase",
                                    fontSize: "smaller",
                                    padding: "0 0 1rem",
                                    display: "inline-block",
                                  }}
                                />
                                <div className="fatv2-masthead-form-row">
                                  <SelectField
                                    label={
                                      isSC
                                        ? "上课地点"
                                        : isTC
                                          ? "上課地點"
                                          : "Lesson Location"
                                    }
                                    name={"teachingLocation"}
                                    idField={"lesson-location"}
                                    required={false}
                                    onChangeHandler={handleInputChange}
                                    externalDataSource={"lesson-locations"}
                                    labelClassName={"fatv2-masthead-form-label"}
                                    selectClassName={
                                      "form-select fatv2-masthead-form-select"
                                    }
                                  />
                                </div>
                                <div className="fatv2-masthead-form-row tps-form-label">
                                  <TextField
                                    label={
                                      isSC
                                        ? "老师的名字"
                                        : isTC
                                          ? "老師的名字"
                                          : "Teacher Name"
                                    }
                                    onChangeHandler={handleInputChange}
                                    idField={"fullName"}
                                    name={"fullName"}
                                    placeholder={
                                      isSC
                                        ? "输入名字"
                                        : isTC
                                          ? "輸入名字"
                                          : "Enter a name"
                                    }
                                    required={false}
                                    labelClassName={"fatv2-masthead-form-label"}
                                    inputClassName={
                                      "form-text fatv2-masthead-form-input"
                                    }
                                  />
                                </div>
                                <div className="tps-form-controls">
                                  <div className="fatv2-masthead-form-row fatv2-masthead-button-wrapper">
                                    <input
                                      className="button fatv2-masthead-button fatv2-masthead-button-submit"
                                      type="submit"
                                      value={
                                        isSC ? "搜索" : isTC ? "搜索" : "Submit"
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </fieldset>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Masthead;
